import { useState, useMemo, useCallback, useEffect } from 'react';
import { generateUserAgentValues } from '@financial/app-js-interfaces';
import { useCookie, useVisibilityChange } from '@financial/common-hooks';
import { verifyNaverSignInstalledEnv, scanRemoteApps, scanMalwareApps } from './app-js-interfaces/NaverSign.mjs';
import { ERROR_CODE } from './constant.mjs';
import { sendToNelo } from './sendToNelo.mjs';
import { isAppCheckDisabled } from './utils.mjs';

const COOKIE_KEY = '__MSS_VALIDATOR_LATEST_CHECK_DATE__';
const useMSSValidator = props => {
  const {
    disableCheck = false,
    checkIntervalMS = NaN
  } = props;
  const [isChecked, setIsChecked] = useState(false);
  const [value, updateCookie] = useCookie(COOKIE_KEY);
  const [deviceCode, setDeviceCode] = useState();
  const [activeRemoteAppNames, setActiveRemoteAppNames] = useState([]);
  const checkingCode = useMemo(() => {
    if ('code' in props) {
      return props.code;
    }
    return props.check.environment;
  }, [props]);
  const remoteApp = useMemo(() => {
    if ('code' in props) {
      return true;
    }
    return props.check.remoteApp;
  }, [props]);
  const malwareApp = useMemo(() => {
    if ('code' in props) {
      return true;
    }
    return props.check.malwareApp;
  }, [props]);
  const checkDeviceEnvironment = useCallback(async () => {
    if (typeof checkingCode !== 'number') {
      return;
    }
    const {
      isNaverApp
    } = generateUserAgentValues(window.navigator.userAgent);
    if (isNaverApp) {
      try {
        const result = await verifyNaverSignInstalledEnv(checkingCode);
        return result;
      } catch (e) {
        return ERROR_CODE.알수없음;
      }
    }
  }, [checkingCode]);
  const checkRemoteApp = useCallback(async () => {
    if (!remoteApp) {
      return;
    }
    const {
      isNaverApp,
      isAndroid
    } = generateUserAgentValues(window.navigator.userAgent);
    if (isNaverApp && isAndroid) {
      try {
        const result = await scanRemoteApps();
        const activeAppNames = result.items.filter(item => item.isActive).map(item => item.name);
        return activeAppNames;
      } catch {
        //
      }
    }
  }, [remoteApp]);
  const checkMalwareApp = useCallback(() => {
    if (!malwareApp) {
      return;
    }
    const {
      isNaverApp,
      isAndroid
    } = generateUserAgentValues(window.navigator.userAgent);
    if (isNaverApp && isAndroid) {
      scanMalwareApps();
    }
  }, [malwareApp]);
  const validateMSS = useCallback(async function () {
    let force = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    // disableCheck 가 true 이거나, 앱체크가 비활성화 되어있으면 실행하지 않습니다.
    if (disableCheck || isAppCheckDisabled()) {
      return;
    }

    // 쿠키에 아직 값이 존재하고, checkIntervalMS 값이 유효하다면 실행하지 않습니다.
    if (value && !Number.isNaN(checkIntervalMS)) {
      return;
    }

    // 강제 요청 또는 체크한 적이 없다면 체크를 실행합니다.
    if (force || !isChecked) {
      const [deviceCodeResult, activeRemoteAppNameResult] = await Promise.all([checkDeviceEnvironment(), checkRemoteApp()]);
      if (deviceCodeResult) {
        setDeviceCode(deviceCodeResult);
        sendToNelo({
          resultCode: deviceCodeResult
        });
      }
      if (activeRemoteAppNameResult) {
        setActiveRemoteAppNames(activeRemoteAppNameResult);
        sendToNelo({
          resultCode: activeRemoteAppNameResult
        });
      }
      if (deviceCodeResult === ERROR_CODE.성공 && activeRemoteAppNameResult?.length === 0) {
        checkMalwareApp();
      }
      setIsChecked(true);
    }
  }, [checkDeviceEnvironment, checkIntervalMS, checkMalwareApp, checkRemoteApp, disableCheck, isChecked, value]);

  /**
   * @description checkIntervalMS 가 있고, 체크를 한번 수행하면 현재 시간  + checkIntervalMS 기준 만 유효한 쿠키를 생성합니다.
   */
  useEffect(() => {
    if (isChecked && checkIntervalMS) {
      const expireDate = new Date();
      expireDate.setMilliseconds(expireDate.getMilliseconds() + checkIntervalMS);
      updateCookie(new Date().toISOString(), {
        expires: expireDate.getTime()
      });
    }
  }, [isChecked, updateCookie, checkIntervalMS]);
  useEffect(() => {
    if (!isChecked) {
      validateMSS();
    }
  }, [validateMSS, isChecked]);
  useVisibilityChange(isVisible => {
    if (isVisible) {
      validateMSS(true);
    }
  }, false);
  return useMemo(() => ({
    deviceCode,
    activeRemoteAppNames
  }), [activeRemoteAppNames, deviceCode]);
};

export { useMSSValidator };
